import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { CalendarEntry, ClassroomSummary, DailyToday, MyAccount } from '../../shared/api/types';
import { InstallApp } from '../../shared/ui/InstallApp';
import { PushCard } from '../notifications/NotificationsPage';
import { bibleLink } from '../bible/BiblePage';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { Agenda } from '../calendar/CalendarPage';
import { JoinChurchForm } from '../churches/JoinChurchForm';
import { useMe } from '../session/SessionProvider';
import { relationLabel } from '../shell/ContextSwitcher';
import { AnnouncementBanner } from '../shell/MyChurchPage';
import { loadPending } from './pending';
import { PendingRequests, RejectedRequests, useMyPendingRequests } from '../churches/PendingRequests';
import { ChurchPreview } from '../churches/ChurchPreview';
import { HomeLiveCard } from '../live/LivePage';
import { Dashboards } from './Dashboards';
import { Avatar } from '../../shared/ui/Avatar';
import { useChurchCover } from '../profile/useChurchCover';
import { shortDate } from '../classrooms/shared';
import { placeLabel } from '../classrooms/Grades';

function greeting(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

/** Inicio personalizado: saludo, contexto actual y "Qué tengo pendiente". */
export function HomePage() {
  const [sent, setSent] = useState(0);
  const me = useMe();
  const person = useAsync((signal) => api.get<MyAccount>('/identity/me', signal), [me.personId]);
  const pending = useAsync((signal) => loadPending(me, signal), [me.personId, me.activeChurchId]);
  const church = useChurchCover(me.activeChurchId);
  const cover = church.status === 'ready' && church.data ? church.data.logoUrl : null;

  return (
    <div className="page home">
      <header className="wall-head home-head">
        <div className="wall-cover" style={cover ? { backgroundImage: `url(${cover})` } : undefined} aria-hidden="true" />
        <div className="wall-head__main">
          <Link to="/app/perfil" className="profile-head__photo" aria-label="Mi perfil">
            <Avatar personId={me.personId} name={person.status === 'ready' ? `${person.data.givenName} ${person.data.familyName}` : ''} size={96} />
          </Link>
          <div className="wall-head__name">
            <h1>
              {greeting()}
              {person.status === 'ready' ? `, ${person.data.givenName}` : ''}
            </h1>
            {me.activeContext ? (
              <p className="muted">
                Estás en <strong>{me.activeContext.churchName}</strong> como {relationLabel(me.activeContext.relations).toLowerCase()}.
              </p>
            ) : null}
          </div>
        </div>
      </header>

      {me.activeChurchId && me.can('communication.read') ? <AnnouncementBanner churchId={me.activeChurchId} /> : null}
      {me.contexts.length === 0 ? <PendingCard version={sent} /> : null}
      {me.contexts.length === 0 ? (
        <Card title="Empieza aquí">
          <EmptyState title="Todavía no perteneces a ninguna iglesia en KEORA.">
            <p className="muted">Busca tu iglesia y pide unirte, o usa el código que te dio tu iglesia.</p>
            <Link className="btn btn--primary" to="/iglesias">Encuentra mi iglesia</Link>
          </EmptyState>
          <RejectedRequests />
          <JoinChurchForm onSent={() => { setSent((v) => v + 1); pending.reload(); }} />
          <p className="center">
            <Link to="/app/iglesias/nueva">Registrar mi iglesia</Link>
          </p>
        </Card>
      ) : !me.activeChurchId ? (
        <Card title="Elige dónde actúas">
          <p className="muted">Usa el selector de arriba para elegir la iglesia en la que quieres trabajar ahora.</p>
        </Card>
      ) : null}

      <div className="home__grid">
        <div className="home__main">
          {me.activeChurchId && me.can('church.read') ? <HomeLiveCard /> : null}
          {pending.status === 'ready' && pending.data.length === 0 ? <p className="home-ok">✓ Estás al día. No tienes nada pendiente.</p> : null}
          {pending.status === 'error' ? <ErrorState error={pending.error} onRetry={pending.reload} /> : null}
          {pending.status === 'ready' && pending.data.length > 0 ? (
            <div className="home__focus">
              <Card title="Qué tengo pendiente">
                <ul className="list">
                  {pending.data.map((item) => (
                    <li key={item.key} className="list__item">
                      {item.to ? (
                        <Link to={item.to} className="list__link">
                          <strong>{item.title}</strong>
                          <span className="muted">{item.detail}</span>
                        </Link>
                      ) : (
                        <div className="list__link">
                          <strong>{item.title}</strong>
                          <span className="muted">{item.detail}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          ) : null}
          {me.activeChurchId && me.can('devotional.read') ? <TodayCard churchId={me.activeChurchId} /> : null}
          {me.activeChurchId && me.can('calendar.read') ? (
            <div className="home__slot home__later">
              <UpcomingCard churchId={me.activeChurchId} />
            </div>
          ) : null}
        </div>
        <div className="home__side">
          {me.activeChurchId && me.can('departments.read') ? (
            <div className="home__slot">
              <MyClassroomCard churchId={me.activeChurchId} />
            </div>
          ) : null}
          {me.activeChurchId ? (
            <div className="home__slot home__later">
              <Dashboards churchId={me.activeChurchId} />
            </div>
          ) : null}
          <div className="home__slot home__later">
            <PushCard prompt />
            <InstallApp />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Lo que viene para mí: cultos, eventos, reuniones, clases y oración de los siguientes 14 días. */
function UpcomingCard({ churchId }: { churchId: string }) {
  const upcoming = useAsync((signal) => {
    const from = new Date();
    const to = new Date(from.getTime() + 14 * 24 * 60 * 60 * 1000);
    return api
      .get<{ items: CalendarEntry[] }>(`/calendar/churches/${churchId}/entries?from=${from.toISOString()}&to=${to.toISOString()}`, signal)
      .then((r) => r.items.filter((e) => e.status !== 'canceled').slice(0, 5));
  }, [churchId]);
  return (
    <Card title="Lo que viene" action={<Link to="/app/calendario">Ver agenda</Link>}>
      {upcoming.status === 'loading' ? <Loading /> : null}
      {upcoming.status === 'error' ? <ErrorState error={upcoming.error} onRetry={upcoming.reload} /> : null}
      {upcoming.status === 'ready' && upcoming.data.length === 0 ? <EmptyState title="No hay nada agendado en las próximas dos semanas." /> : null}
      {upcoming.status === 'ready' && upcoming.data.length > 0 ? <Agenda entries={upcoming.data} /> : null}
    </Card>
  );
}

/** Hoy: versículo, oración y reflexión del día (los prepara Pastoral). */
function TodayCard({ churchId }: { churchId: string }) {
  const today = useAsync((signal) => api.get<DailyToday>(`/devotional/churches/${churchId}/daily/today`, signal), [churchId]);
  if (today.status !== 'ready') return null;
  const t = today.data;
  return (
    <section className="today" id="devocional-hoy" aria-labelledby="today-title" ref={(el) => {
      // Desde el aviso de la mañana (/app#devocional-hoy) se lleva a la reflexión.
      if (el && window.location.hash === '#devocional-hoy') el.scrollIntoView?.({ block: 'start' });
    }}>
      <div className="today__head">
        <h2 id="today-title">Hoy</h2>
        {t.canManage ? <Link to="/app/devocional/hoy">Preparar los próximos días</Link> : null}
      </div>
      {t.verse ? (
        <blockquote className="today__verse">
          {t.verse.text ? <p>«{t.verse.text}»</p> : null}
          <footer>
            <Link to={bibleLink(t.verse.label)}>{t.verse.label}</Link>
            {t.verse.translation ? <span className="muted"> · {t.verse.translation}</span> : null}
          </footer>
        </blockquote>
      ) : null}
      {t.hasAudio ? <audio className="today__audio" controls preload="none" src={`/api/devotional/churches/${churchId}/daily/${t.day}/audio`} aria-label="Reflexión en audio" /> : null}
      {t.reflection ? <p className="today__reflection pre-line">{t.reflection}</p> : null}
      {t.prayer ? (
        <div className="today__prayer">
          <strong>Oración del día</strong>
          <p className="pre-line">{t.prayer}</p>
        </div>
      ) : null}
      {t.amen ? <AmenBar churchId={churchId} initial={t.amen} /> : null}
    </section>
  );
}

/** «Amén»: quien leyó o escuchó la promesa de hoy la recibe. El pastor y la administración ven quiénes. */
function AmenBar({ churchId, initial }: { churchId: string; initial: NonNullable<DailyToday['amen']> }) {
  const [amen, setAmen] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState<{ personId: string; name: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const others = amen.count - (amen.mine ? 1 : 0);
  async function say() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ mine: boolean; count: number }>(`/devotional/churches/${churchId}/daily/today/amen`);
      setAmen({ ...amen, ...r });
      if (list) setList(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  async function toggleList() {
    if (list) return setList(null);
    try {
      setList((await api.get<{ items: { personId: string; name: string }[] }>(`/devotional/churches/${churchId}/daily/amens`)).items);
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  return (
    <div className="today__amen">
      {amen.mine ? (
        <span className="today__amen-done">🙏 Dijiste «Amén»{others > 0 ? ` junto con ${others === 1 ? 'otra persona' : `${others} personas más`}` : ''}</span>
      ) : (
        <>
          <Button busy={busy} onClick={() => void say()}>🙏 Amén</Button>
          <span className="small muted">{amen.count > 0 ? `${amen.count === 1 ? 'Una persona ya dijo' : `${amen.count} personas ya dijeron`} «Amén».` : 'Toca «Amén» si recibiste la palabra de hoy.'}</span>
        </>
      )}
      {amen.canSeeList && amen.count > 0 ? (
        <button type="button" className="link-button small" onClick={() => void toggleList()}>
          {list ? 'Ocultar' : `Ver quiénes (${amen.count})`}
        </button>
      ) : null}
      {list ? <p className="small today__amen-list">{list.map((p) => p.name).join(' · ')}</p> : null}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

/** Solicitudes enviadas que esperan aprobación (se ven arriba, no solo en "pendientes"). */
function PendingCard({ version }: { version: number }) {
  const pending = useMyPendingRequests(version);
  if (pending.status !== 'ready' || pending.data.length === 0) return null;
  return (
    <Card title="Tu solicitud está pendiente de aprobación">
      <PendingRequests version={version} />
      <p className="small muted">La iglesia la revisará. Cuando te acepten, aquí aparecerá tu iglesia y podrás usar todo KEORA.</p>
      {pending.data.map((m) => (
        <ChurchPreview key={m.id} churchId={m.church_id} />
      ))}
    </Card>
  );
}

/** Mi salón de Raíces (si soy alumno): su próxima clase, a un toque. */
function MyClassroomCard({ churchId }: { churchId: string }) {
  const all = useAsync(
    (signal) => api.get<{ items: ClassroomSummary[] }>(`/organization/churches/${churchId}/classrooms`, signal).then((r) => r.items).catch(() => []),
    [churchId],
  );
  if (all.status !== 'ready') return null;
  const rooms = { data: all.data.filter((c) => c.role === 'student') };
  const liveRoom = rooms.data.find((c) => c.liveSessionId);
  const teaching = all.data.filter((c) => c.role === 'teacher');
  if (rooms.data.length === 0) {
    if (teaching.length === 0) return null;
    const next = teaching.map((c) => c.nextSession).filter((x) => x !== null).sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
    return (
      <Card title="Panel del maestro" action={<Link to="/app/salones">Raíces</Link>}>
        {next ? (
          <p className="small">
            Próxima clase: {shortDate(next.startsAt)}
            {next.topic ? ` · ${next.topic}` : ''}
          </p>
        ) : null}
        <Link className="btn btn--primary" to="/app/salones/panel">
          Abrir mi panel
        </Link>
      </Card>
    );
  }
  return (
    <>
    {liveRoom ? (
      <section className="live-home" aria-label="Clase en vivo">
        <span className="live-dot">En vivo</span>
        <strong>Tu clase está en vivo · {liveRoom.name}</strong>
        <Link className="btn btn--primary" to={`/app/salones/${liveRoom.id}/clases/${liveRoom.liveSessionId}`}>
          Entrar
        </Link>
      </section>
    ) : null}
    <Card title={rooms.data.length === 1 ? 'Mi salón' : 'Mis salones'} action={<Link to="/app/salones">Raíces</Link>}>
      <ul className="list">
        {rooms.data.map((c) => (
          <li key={c.id} className="list__item">
            <Link className="list__link" to={`/app/salones/${c.id}`}>
              <strong>{c.name}</strong>
              <span className="muted small">
                {c.nextSession ? `Próxima clase: ${shortDate(c.nextSession.startsAt)}${c.nextSession.topic ? ` · ${c.nextSession.topic}` : ''}` : 'Sin clases programadas'}
              </span>
              {c.standing && c.standing.of > 1 ? (
                <span className="small">
                  {placeLabel(c.standing.position, c.standing.points)} Vas en el {c.standing.position}.º lugar de {c.standing.of} · {c.standing.points} pts
                </span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
      {rooms.data.some((c) => c.standing) ? (
        <p className="small">
          <Link to={`/app/salones/${rooms.data[0]!.id}?ver=posiciones`}>Ver la tabla de posiciones</Link>
        </p>
      ) : null}
    </Card>
    </>
  );
}
